import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { CustomizerContext } from "../context/CustomizerContext";
import { getDataJson } from "../helpers/GetDataJson";
import { useSearchTerm } from "./SearchContext";
import * as Y from 'yjs'

const PreviewContext = createContext(null);

const usePreview = () => useContext(PreviewContext);

const hotReloadPage = (iframeRef, data, theme, general) => {
  const allFiles = {
    data,
    theme: theme.toString(),
  };
  if (general) {
    allFiles.general = general?.toJSON();
  }
  if (iframeRef?.current) {
    iframeRef.current.contentWindow.postMessage(
      {
        action: "hot-reload",
        ...allFiles,
      },
      "*"
    );
  } else {
    console.error("Iframe not loaded");
  }
};

const startInspection = (iframeRef) => {
  iframeRef.current.contentWindow.postMessage(
    {
      action: "start-inspection",
    },
    "*"
  );
};

const goToPage = (iframeRef, url) => {
  iframeRef.current.contentWindow.postMessage(
    {
      action: "go-to-page",
      url,
    },
    "*"
  );
};

const PreviewContextProvider = ({ children }) => {
  const iframeRef = useRef();
  const { document } = useContext(CustomizerContext);
  const currentData = useSelector((state) => state.selectedLanguageData);
  const data = document && document.getMap("data");
  const theme = document && document.get("theme.draft", Y.XmlText);
  const general = document && document.getMap("general");
  const { setSearchTerm } = useSearchTerm();

  const methods = useMemo(
    () => ({
      iframeRef,
      hotReloadPage: () => {
        const dataJson = getDataJson({ currentData, data, document });
        hotReloadPage(iframeRef, dataJson, theme, general);
      },
      startInspection: () => startInspection(iframeRef),
      goToPage: (url) => goToPage(iframeRef, url),
    }),
    [theme, currentData, data, general, document]
  );

  useEffect(() => {
    const listener = (event) => {
      const data = event.data;
      if (data.action === "path-selected") {
        setSearchTerm({ path: data.path });
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [setSearchTerm]);

  return (
    <PreviewContext.Provider value={methods}>
      {children}
    </PreviewContext.Provider>
  );
};

export { usePreview, PreviewContextProvider };
